import type { UpdateGeneratedPodcast } from '@kimuno/api-sdk/lib/types/requests/Ai';
import {
	type GeneratedPodcast,
	GeneratedPodcastStageStatus,
} from '@kimuno/api-sdk/lib/types/responses/Ai';
import type { Editor } from '@tiptap/react';
import { debounce } from 'debounce';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
	clearActiveDocument,
	clearActivePodcast,
	getGeneratedDocument,
	setActiveDocumentId,
	setActivePodcastId,
	updateGeneratedPodcast,
} from '../actions/aiAssistant.actions';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import { ActiveView, TITLE_SAVE_DEBOUNCE_MS } from '../components/AIAssistant/shared/constants';
import {
	podcastFormatMap,
	podcastLanguageMap,
} from '../components/AIAssistant/shared/TemplatePromptForm/AIAssistantTemplatePromptForm';
import { convertMarkdownToHtml } from '../components/utils/contentConverter';
import { routes } from '../constants';
import {
	getActiveDocumentId,
	getActivePodcastId,
	getConversationDocument,
	getCurrentConversation,
	getDocumentById,
} from '../reducers/aiAssistant.selectors';
import services from '../services';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';
import useRouter from '../utils/useRouter';
import { aiAssistantContext } from './AIAssistantContextProvider';

// Value → label lookups, the inverse of the prompt-form maps, used to turn a stored
// podcast's raw format/language values back into their display labels.
const reversePodcastFormatMap: Record<string, string> = Object.fromEntries(
	Object.entries(podcastFormatMap).map(([label, value]) => [value, label])
);
const reversePodcastLanguageMap: Record<string, string> = Object.fromEntries(
	Object.entries(podcastLanguageMap).map(([label, value]) => [value, label])
);

export enum AIAssistantEditorMode {
	PLAYGROUND = 'PLAYGROUND',
	STREAM = 'STREAM',
	PREVIOUS = 'PREVIOUS',
	PODCAST = 'PODCAST',
}

export interface TextEditorData {
	id: string | null;
	title: string;
	content: string;
}

export interface PodcastDetailsData {
	id: string;
	title: string;
	subject: string;
	grade: string;
	language: string;
	format: string;
	transcript: string;
	audioUrl?: string;
	durationLabel?: string;
	audioGenerationFailed?: boolean;
	audioStatus?: GeneratedPodcastStageStatus;
}

export interface EditorCloseScrollTarget {
	messageId: string | null;
	documentId: string | null;
	podcastId: string | null;
	// 'open' scrolls once the editor is open; 'close' after it closed and cards re-expanded.
	phase: 'open' | 'close';
}

export interface AIAssistantEditorContext {
	isTextEditorOpen: boolean;
	mode: AIAssistantEditorMode;
	openTextEditor: (options?: { messageId?: string; documentId?: string }) => void;
	openPodcastDetails: (podcast: PodcastDetailsData) => void;
	openPodcast: (options: { podcastId: string; autoPlay?: boolean }) => void;
	podcastDetails: PodcastDetailsData | null;
	autoPlayPodcast: boolean;
	consumeAutoPlayPodcast: () => void;
	closeTextEditor: () => void;
	messageId: string | null;
	documentId: string | null;
	activePodcastId: string | null;
	editorCloseScrollTarget: EditorCloseScrollTarget | null;
	clearEditorCloseScrollTarget: () => void;
	activeDocumentCardRef: React.RefObject<HTMLDivElement>;
	activeImageRef: React.RefObject<HTMLDivElement>;
	imageScrollTarget: string | null;
	clearImageScrollTarget: () => void;
	editorData: TextEditorData;
	setTextEditorContent: (content: string) => void;
	textEditorRef: React.MutableRefObject<Editor | null> | null;
	hasUnsavedChanges: boolean;
	dismissChangeIndicator: () => void;
	markContentChanged: () => void;
	markTitleChanged: () => void;
	updatePodcastTitle: (title: UpdateGeneratedPodcast['title']) => void;
	flushPodcastTitleSave: () => void;
}

export const aiAssistantEditorContext = createContext<AIAssistantEditorContext>({
	isTextEditorOpen: false,
	mode: AIAssistantEditorMode.PLAYGROUND,
	openTextEditor: () => {},
	openPodcastDetails: () => {},
	openPodcast: () => {},
	podcastDetails: null,
	autoPlayPodcast: false,
	consumeAutoPlayPodcast: () => {},
	closeTextEditor: () => {},
	messageId: null,
	documentId: null,
	activePodcastId: null,
	editorCloseScrollTarget: null,
	clearEditorCloseScrollTarget: () => {},
	activeDocumentCardRef: { current: null },
	activeImageRef: { current: null },
	imageScrollTarget: null,
	clearImageScrollTarget: () => {},
	editorData: {
		id: null,
		title: '',
		content: '',
	},
	setTextEditorContent: () => {},
	textEditorRef: null,
	hasUnsavedChanges: false,
	dismissChangeIndicator: () => {},
	markContentChanged: () => {},
	markTitleChanged: () => {},
	updatePodcastTitle: () => {},
	flushPodcastTitleSave: () => {},
});

interface AIAssistantEditorProviderProps {
	variant?: 'page' | 'dialog';
}

const AIAssistantEditorProvider: React.FC<AIAssistantEditorProviderProps> = ({
	children,
	variant = 'page',
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const {
		setIsExtended,
		isAssistantOpen,
		activeView,
		isExtended,
		readyPodcast,
		clearReadyPodcast,
		clearPendingPodcastDetails,
		pendingPodcastIdRef,
	} = useContext(aiAssistantContext);
	const {
		router,
		match: {
			location: { pathname, query },
		},
	} = useRouter();
	const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
	const [mode, setMode] = useState<AIAssistantEditorMode>(AIAssistantEditorMode.PLAYGROUND);
	const [messageId, setMessageId] = useState<string | null>(null);
	const textEditorRef = useRef<Editor | null>(null);
	const lastResolvedDataRef = useRef<TextEditorData | null>(null);
	const pendingDocumentIdRef = useRef<string | null>(null);
	const shouldRetryRef = useRef(false);
	const documentId = useSelector(getActiveDocumentId);
	const activePodcastId = useSelector(getActivePodcastId);
	const streamDocument = useSelector(getConversationDocument);
	const effectiveDocumentId = pendingDocumentIdRef.current || documentId;
	const historyDocument = useSelector((state) =>
		effectiveDocumentId ? getDocumentById(state, effectiveDocumentId) : undefined
	);
	const currentConversation = useSelector(getCurrentConversation);
	const [editorData, setEditorData] = useState<TextEditorData>({
		id: null,
		title: '',
		content: '',
	});
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [podcastDetails, setPodcastDetails] = useState<PodcastDetailsData | null>(null);
	// Armed only by the chat-card play button; every other opener leaves it false so
	// opens from Meine Inhalte, "Details anzeigen", direct URL, or auto-open never autoplay.
	const [autoPlayPodcast, setAutoPlayPodcast] = useState(false);
	const consumeAutoPlayPodcast = useCallback(() => setAutoPlayPodcast(false), []);
	const podcastModeRef = useRef(false);
	const originalDataRef = useRef<TextEditorData | null>(null);
	const [editorCloseScrollTarget, setEditorCloseScrollTarget] =
		useState<EditorCloseScrollTarget | null>(null);
	const activeDocumentCardRef = useRef<HTMLDivElement>(null);
	const activeImageRef = useRef<HTMLDivElement>(null);
	const [imageScrollTarget, setImageScrollTarget] = useState<string | null>(null);
	const clearEditorCloseScrollTarget = useCallback(() => setEditorCloseScrollTarget(null), []);
	const clearImageScrollTarget = useCallback(() => setImageScrollTarget(null), []);
	const lastPodcastConversationIdRef = useRef<string | null>(null);
	// The podcast that last opened in that conversation — a different podcastId
	// arriving on the same conversation is a new request, not a repeat.
	const lastOpenedPodcastIdRef = useRef<string | null>(null);
	// The generating podcast a loading-view scroll target was already set for — once per
	// podcast, or the poll's conversation updates would re-set the consumed target and
	// drag the chat back to the card on every tick (WF-64).
	const loadingViewScrollPodcastIdRef = useRef<string | null>(null);
	// Leaving the loading view with its target still unconsumed (e.g. small screens mount
	// no chat pane) must drop it, or the completed-podcast handover would consume it and
	// scroll — that handover is deliberately scroll-free. Unrelated targets stay put.
	const releaseLoadingViewScrollTarget = useCallback(() => {
		const loadingPodcastId = loadingViewScrollPodcastIdRef.current;
		if (!loadingPodcastId) return;
		loadingViewScrollPodcastIdRef.current = null;
		setEditorCloseScrollTarget((prev) =>
			prev && prev.phase === 'open' && prev.podcastId === loadingPodcastId ? null : prev
		);
	}, []);
	// Latest close-target inputs behind a ref so closeTextEditor keeps a stable identity —
	// the auto-close effects below re-fire on it, and a re-created handler makes them close
	// (and clear shared Redux selection) right after something opens.
	const closeTargetSourceRef = useRef({
		podcastDetailsId: null as string | null,
		activePodcastId: null as string | null,
		documentId: null as string | null,
		messageId: null as string | null,
	});
	closeTargetSourceRef.current = {
		podcastDetailsId: podcastDetails?.id ?? null,
		activePodcastId,
		documentId,
		messageId,
	};

	const setTextEditorContent = (content: string) => {
		setEditorData({
			...editorData,
			content,
		});
	};

	const markContentChanged = useCallback(() => {
		setHasUnsavedChanges(true);
	}, []);

	const markTitleChanged = useCallback(() => {
		setHasUnsavedChanges(true);
	}, []);

	const debouncedPodcastSave = useRef(
		debounce((podcastId: string, title: string) => {
			dispatch(updateGeneratedPodcast({ podcastId, title }));
		}, TITLE_SAVE_DEBOUNCE_MS)
	).current;

	// Optimistically update the local title, then debounce-persist — mirrors handleTitleChange for documents.
	const updatePodcastTitle = useCallback(
		(title: UpdateGeneratedPodcast['title']) => {
			setPodcastDetails((prev) => (prev ? { ...prev, title } : prev));
			if (podcastDetails?.id) {
				debouncedPodcastSave(podcastDetails.id, title);
			}
		},
		[podcastDetails?.id, debouncedPodcastSave]
	);

	// Persists a pending debounced title save immediately.
	const flushPodcastTitleSave = useCallback(() => {
		debouncedPodcastSave.flush?.();
	}, [debouncedPodcastSave]);

	const dismissChangeIndicator = useCallback(() => {
		setHasUnsavedChanges(false);
		originalDataRef.current = {
			...editorData,
		};
	}, [editorData]);

	const openTextEditor = (options?: { messageId?: string; documentId?: string }) => {
		podcastModeRef.current = false;
		// Opening a document/answer leaves podcast selection — clear it so the podcast
		// and document cards can't both stay highlighted (mirrors enterPodcastMode's clearActiveDocument).
		dispatch(clearActivePodcast());
		setIsExtended(true);
		// Set the target to this open's intent, or clear it when opening without options
		// (STREAM) — otherwise a residual target from a previous open could be reused here.
		setEditorCloseScrollTarget(
			options?.messageId || options?.documentId
				? {
						messageId: options?.messageId ?? null,
						documentId: options?.documentId ?? null,
						podcastId: null,
						phase: 'open',
					}
				: null
		);
		if (options?.messageId) {
			setMode(AIAssistantEditorMode.PLAYGROUND);
			setMessageId(options.messageId);
			pendingDocumentIdRef.current = null;
			dispatch(clearActiveDocument());
		} else if (options?.documentId) {
			setMode(AIAssistantEditorMode.PREVIOUS);
			pendingDocumentIdRef.current = options.documentId;
			shouldRetryRef.current = true;
			dispatch(setActiveDocumentId({ documentId: options.documentId }));
			setMessageId(null);
		} else {
			setMode(AIAssistantEditorMode.STREAM);
			setMessageId(null);
			pendingDocumentIdRef.current = null;
			dispatch(clearActiveDocument());
		}
		// Don't set isTextEditorOpen here - let the effect on line 233 handle it
		// once we have actual content to display (line 248-256)
	};

	// Turn a stored podcast into the detail view model. Metadata comes from the
	// response's flattened generation params (WOR-977).
	const buildPodcastDetailsData = useCallback(
		(podcastId: string, podcast: GeneratedPodcast): PodcastDetailsData => {
			const durationSec = podcast.audio?.durationSec;
			const durationLabel = durationSec
				? `${Math.floor(durationSec / 60)}:${String(durationSec % 60).padStart(2, '0')}`
				: undefined;
			const formatValue = podcast.format ?? '';
			const languageValue = podcast.language ?? '';
			return {
				id: podcastId,
				title: podcast.title,
				subject: podcast.subject ?? '',
				grade: podcast.gradeLevel ? `${podcast.gradeLevel}. ${t('Class year')}` : '',
				language: languageValue
					? t(reversePodcastLanguageMap[languageValue] ?? languageValue.toUpperCase())
					: '',
				format: formatValue ? t(reversePodcastFormatMap[formatValue] ?? formatValue) : '',
				transcript: podcast.script?.content ?? '',
				audioUrl: podcast.audio?.url,
				durationLabel,
				audioGenerationFailed: podcast.status === GeneratedPodcastStageStatus.ERROR,
				audioStatus: podcast.audio?.status,
			};
		},
		[t]
	);

	const enterPodcastMode = () => {
		podcastModeRef.current = true;
		// Any podcast open satisfies this conversation's auto-open — without this mark,
		// the auto-open effect re-opens the podcast right after closeTextEditor.
		if (currentConversation?.data?.id) {
			lastPodcastConversationIdRef.current = currentConversation.data.id;
		}
		setIsExtended(true);
		setMode(AIAssistantEditorMode.PODCAST);
		setMessageId(null);
		pendingDocumentIdRef.current = null;
		dispatch(clearActiveDocument());
	};

	const openPodcastDetails = (podcast: PodcastDetailsData) => {
		enterPodcastMode();
		lastOpenedPodcastIdRef.current = podcast.id;
		setAutoPlayPodcast(false);
		dispatch(setActivePodcastId({ podcastId: podcast.id }));
		setPodcastDetails(podcast);
		setEditorData({ id: null, title: podcast.title, content: '' });
		setIsTextEditorOpen(true);
	};

	// Open a podcast by id (from a chat card). Mirrors documents: select the active id
	// synchronously so the correct card highlights immediately, then resolve its details async.
	const openPodcast = ({
		podcastId,
		autoPlay = false,
	}: {
		podcastId: string;
		autoPlay?: boolean;
	}) => {
		enterPodcastMode();
		lastOpenedPodcastIdRef.current = podcastId;
		setAutoPlayPodcast(autoPlay);
		dispatch(setActivePodcastId({ podcastId }));
		setIsTextEditorOpen(true);
		// Mirrors openTextEditor: the auto-scroll hook brings this podcast's chat card into view.
		setEditorCloseScrollTarget({
			messageId: null,
			documentId: null,
			podcastId,
			phase: 'open',
		});

		// Details for this podcast are already loaded (chat card / prior open), so skip the
		// re-fetch: it would rebuild the labels and swap the audio URL out from under an autoplay.
		if (podcastDetails?.id === podcastId) {
			return;
		}

		services.ai
			.getGeneratedPodcast(podcastId, { includeScriptText: true })
			.then((podcast) => {
				setPodcastDetails(buildPodcastDetailsData(podcastId, podcast));
			})
			.catch((error) => {
				notifyBugsnagNetwork('AIAssistant', 'openPodcast', {
					podcastId,
					error,
				});
			});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const closeTextEditor = useCallback(() => {
		// Target whatever was open so the chat scrolls back to its card; derived before the resets.
		const closing = closeTargetSourceRef.current;
		const closingPodcastId = podcastModeRef.current
			? (closing.podcastDetailsId ?? closing.activePodcastId)
			: null;
		if (closingPodcastId) {
			setEditorCloseScrollTarget({
				messageId: null,
				documentId: null,
				podcastId: closingPodcastId,
				phase: 'close',
			});
		} else if (closing.documentId) {
			setEditorCloseScrollTarget({
				messageId: null,
				documentId: closing.documentId,
				podcastId: null,
				phase: 'close',
			});
		} else if (closing.messageId) {
			setEditorCloseScrollTarget({
				messageId: closing.messageId,
				documentId: null,
				podcastId: null,
				phase: 'close',
			});
		} else {
			// Nothing identifiable open (e.g. stream editor) — drop any pending target.
			clearEditorCloseScrollTarget();
		}
		setIsTextEditorOpen(false);
		setMessageId(null);
		setAutoPlayPodcast(false);
		setEditorData({
			id: null,
			title: '',
			content: '',
		});
		lastResolvedDataRef.current = null;
		pendingDocumentIdRef.current = null;
		shouldRetryRef.current = false;
		setMode(AIAssistantEditorMode.PLAYGROUND);
		setIsExtended(false);
		setHasUnsavedChanges(false);
		// The chat card still needs podcastDetails after close; the auto-open effect does not re-fetch.
		if (!podcastModeRef.current) {
			setPodcastDetails(null);
		}
		podcastModeRef.current = false;
		dispatch(clearActivePodcast());
		originalDataRef.current = null;
		dispatch(clearActiveDocument());

		if (query?.edit || query?.id || query?.podcastId) {
			router.replace(pathname);
		}
	}, [
		setIsTextEditorOpen,
		setMessageId,
		setEditorData,
		setMode,
		setIsExtended,
		clearEditorCloseScrollTarget,
		query,
		router,
		pathname,
		dispatch,
	]);

	const isEditModeFromUrl = query?.edit === 'true' && !!query?.id;
	const imageIdFromUrl = query?.imageId;
	const podcastIdFromUrl = query?.podcastId;

	// The always-mounted dialog instance must not react to state owned by the page instance while closed.
	const isDormant = variant === 'dialog' && !isAssistantOpen;

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (isDormant) return;
		const isAIToolsPage = pathname.startsWith(`/${routes.aiTools.root}`);

		if (isAIToolsPage && isEditModeFromUrl && activeView === ActiveView.CHAT) {
			openTextEditor({ documentId: query.id });
		}
	}, [pathname, activeView, isEditModeFromUrl, query?.id, isDormant]);

	// Auto-open podcast details when a podcast conversation becomes active.
	// readyPodcast is set by the polling effect after a watched generation completes;
	// the podcastId URL param carries explicit intent (Meine Inhalte navigation,
	// bookmark, refresh). A plain conversation URL opens nothing — mirroring documents.
	//
	// Bails marked "not yet" keep the pending flag armed because a detail is still coming;
	// every other bail must release it, or the handover holds the loading view over a
	// finished podcast forever.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (isDormant) return; // not yet
		// Not yet: the flip to CHAT is what starts the handover, and it lands a commit later.
		if (activeView !== ActiveView.CHAT) {
			// A generating podcast (Meine Inhalte click, mid-generation refresh) renders the
			// loading pane instead of the editor, so the CHAT handover below never runs for it —
			// target its chat card here so the pane still scrolls to the highlighted card (WF-64).
			if (activeView === ActiveView.PODCAST_LOADING) {
				const loadingPodcastId = pendingPodcastIdRef.current || podcastIdFromUrl;
				if (loadingPodcastId && loadingViewScrollPodcastIdRef.current !== loadingPodcastId) {
					loadingViewScrollPodcastIdRef.current = loadingPodcastId;
					setEditorCloseScrollTarget({
						messageId: null,
						documentId: null,
						podcastId: loadingPodcastId,
						phase: 'open',
					});
				}
			} else {
				releaseLoadingViewScrollTarget();
			}
			return;
		}
		releaseLoadingViewScrollTarget();

		const messages = currentConversation?.data?.messages;
		const conversationId = currentConversation?.data?.id;
		// Already showing podcast details, so nothing is pending any more. Heal a missing
		// handled-mark (podcast opened before the conversation had loaded) — but never
		// overwrite one, or navigating away would skip the cross-conversation close.
		if (mode === AIAssistantEditorMode.PODCAST) {
			if (conversationId && !lastPodcastConversationIdRef.current) {
				lastPodcastConversationIdRef.current = conversationId;
			}
			clearPendingPodcastDetails();
			return;
		}
		// An explicit document-edit URL wins over podcast auto-open; the conversation counts
		// as handled so closing that document returns to chat, not to an uninvited podcast.
		if (isEditModeFromUrl) {
			if (conversationId) lastPodcastConversationIdRef.current = conversationId;
			clearPendingPodcastDetails();
			return;
		}

		if (!messages || !conversationId) return; // not yet

		const podcastId = pendingPodcastIdRef.current || readyPodcast?.id || podcastIdFromUrl;
		// Once a podcast has opened in this conversation, its auto-open is used up for
		// re-runs (e.g. right after a close) — but only for that same podcast: a different
		// podcastId arriving on the same conversation is a new request and must still open.
		const autoOpenAlreadyConsumed = lastPodcastConversationIdRef.current === conversationId;
		const isAlreadyOpenedPodcast = podcastId === lastOpenedPodcastIdRef.current;
		if (!podcastId || (autoOpenAlreadyConsumed && isAlreadyOpenedPodcast)) {
			clearPendingPodcastDetails();
			return;
		}

		// The poll already fetched the settled payload, so open from it synchronously — that
		// keeps the handover inside one commit instead of spanning another round trip.
		if (readyPodcast && readyPodcast.id === podcastId) {
			openPodcastDetails(buildPodcastDetailsData(podcastId, readyPodcast));
			// Consume the handover payload: left armed, it would re-open this podcast
			// under the next editor close in the same conversation.
			clearReadyPodcast();
			clearPendingPodcastDetails();
			return;
		}

		let cancelled = false;

		services.ai
			.getGeneratedPodcast(podcastId, { includeScriptText: true })
			.then((podcast) => {
				// User navigated away before fetch completed — discard result.
				if (cancelled) return;

				// skipping the ref mark lets the effect retry after the poll settles
				if (podcast.status === GeneratedPodcastStageStatus.LOADING) {
					clearPendingPodcastDetails();
					return;
				}

				// openPodcastDetails marks the conversation handled — only on success, so a
				// transient failure can retry.
				openPodcastDetails(buildPodcastDetailsData(podcastId, podcast));
				// Page entry / refresh scrolls the chat to the podcast card (documents do the
				// same via their edit URL). The readyPodcast handover above deliberately doesn't —
				// documents don't scroll after streaming either.
				setEditorCloseScrollTarget({
					messageId: null,
					documentId: null,
					podcastId,
					phase: 'open',
				});
				clearPendingPodcastDetails();
			})
			.catch((error) => {
				if (cancelled) return;
				clearPendingPodcastDetails();
				notifyBugsnagNetwork('AIAssistant', 'fetchPodcastDetails', {
					podcastId,
					error,
				});
			});

		return () => {
			cancelled = true;
		};
	}, [
		currentConversation?.data?.messages,
		currentConversation?.data?.id,
		mode,
		activeView,
		readyPodcast,
		isDormant,
		isEditModeFromUrl,
		podcastIdFromUrl,
	]);

	// Close the podcast detail when the active conversation changes, so it doesn't
	// linger after navigating away via routes that don't call closeTextEditor()
	// (e.g. the sidebar link, which keeps this page-scoped provider mounted).
	const activeConversationId = currentConversation?.data?.id ?? null;
	useEffect(() => {
		if (isDormant) return;
		if (
			podcastModeRef.current &&
			lastPodcastConversationIdRef.current &&
			activeConversationId !== lastPodcastConversationIdRef.current
		) {
			lastPodcastConversationIdRef.current = null;
			closeTextEditor();
		}
	}, [activeConversationId, closeTextEditor, isDormant]);

	useEffect(() => {
		if (!imageIdFromUrl) return;

		const messages = currentConversation?.data?.messages;
		if (!messages) return;

		const targetMessage = messages.find((msg) => msg.context?.generatedImageId === imageIdFromUrl);

		if (targetMessage?.id) {
			setImageScrollTarget(targetMessage.id);
			router.replace(pathname);
		}
	}, [imageIdFromUrl, currentConversation?.data?.messages, pathname, router]);

	const resolveEditorData = useCallback(() => {
		const defaultData = {
			id: null,
			title: '',
			content: '',
			rawContent: '',
		};

		switch (mode) {
			case AIAssistantEditorMode.PLAYGROUND: {
				if (!messageId || !currentConversation?.data?.messages) return defaultData;
				let messageIndex = 0;
				const foundMessage = currentConversation?.data?.messages.find((message, index) => {
					if (message.id === messageId) {
						messageIndex = index;
						return true;
					}
					return false;
				});
				if (!foundMessage) return defaultData;

				const previousUserMessage =
					currentConversation?.data?.messages[messageIndex - 1].content[0]?.text ?? '';
				const assistantMessage = foundMessage.content[0]?.text ?? '';

				return {
					id: null,
					title: t('Answer for:') + ' ' + previousUserMessage,
					content: '',
					rawContent: assistantMessage,
				};
			}
			case AIAssistantEditorMode.PREVIOUS: {
				if (!historyDocument?.data) return defaultData;
				return {
					id: historyDocument.data.id,
					title: historyDocument.data.title,
					content: '',
					rawContent: historyDocument.data.content,
				};
			}
			case AIAssistantEditorMode.STREAM: {
				if (!streamDocument) return defaultData;
				return {
					id: streamDocument.id,
					title: streamDocument.title,
					content: '',
					rawContent: streamDocument.content,
				};
			}
			case AIAssistantEditorMode.PODCAST: {
				if (!podcastDetails) return defaultData;
				return {
					id: null,
					title: podcastDetails.title,
					content: '',
					rawContent: '',
				};
			}
			default:
				return defaultData;
		}
	}, [mode, messageId, streamDocument, historyDocument, currentConversation, podcastDetails, t]);

	// Memoize markdown-to-HTML conversion to only happen when text editor is actually open
	// This prevents expensive conversions during streaming when the editor is not visible
	const convertedHtmlContent = useMemo(() => {
		const rawData = resolveEditorData();
		if (!rawData.rawContent) {
			return '';
		}
		return convertMarkdownToHtml(rawData.rawContent);
	}, [resolveEditorData]);

	useEffect(() => {
		if (mode === AIAssistantEditorMode.PREVIOUS && effectiveDocumentId) {
			const documentStatus = historyDocument?.status;
			const isNotReady =
				!documentStatus ||
				(documentStatus !== AsyncActionStatus.READY &&
					documentStatus !== AsyncActionStatus.LOADING &&
					documentStatus !== AsyncActionStatus.ERROR);
			const shouldRetry = documentStatus === AsyncActionStatus.ERROR && shouldRetryRef.current;

			if (isNotReady || shouldRetry) {
				shouldRetryRef.current = false;
				dispatch(getGeneratedDocument({ documentId: effectiveDocumentId }));
			}
			// Clear pending ref once Redux has caught up
			if (pendingDocumentIdRef.current && documentId === pendingDocumentIdRef.current) {
				pendingDocumentIdRef.current = null;
			}
		}
	}, [mode, effectiveDocumentId, documentId, historyDocument?.status, dispatch]);

	useEffect(() => {
		const data = resolveEditorData();
		const dataWithHtml = {
			...data,
			content: convertedHtmlContent,
		};

		const lastData = lastResolvedDataRef.current;
		if (
			!lastData ||
			dataWithHtml.id !== lastData.id ||
			dataWithHtml.title !== lastData.title ||
			dataWithHtml.content !== lastData.content
		) {
			lastResolvedDataRef.current = dataWithHtml;
			setEditorData(dataWithHtml);
			originalDataRef.current = { ...dataWithHtml };
			setHasUnsavedChanges(false);

			// Check if we have content OR if we have a title (indicating data was resolved)
			const hasValidData = dataWithHtml.content || dataWithHtml.title;
			// openPodcastDetails opens the podcast pane itself; auto-opening here would
			// undo a cross-conversation close through this effect's stale closure.
			if (hasValidData && mode !== AIAssistantEditorMode.PODCAST) {
				setIsTextEditorOpen(true);
				setIsExtended(true);
			}
		}
	}, [resolveEditorData, convertedHtmlContent, setIsExtended, mode]);

	useEffect(() => {
		if (
			!isAssistantOpen &&
			!isEditModeFromUrl &&
			!imageIdFromUrl &&
			// Entry intent like edit/imageId: the podcast auto-open is still in flight on
			// mount, and this close would strip the param before it lands.
			!podcastIdFromUrl &&
			!podcastModeRef.current
		) {
			closeTextEditor();
		}
	}, [isAssistantOpen, isEditModeFromUrl, imageIdFromUrl, podcastIdFromUrl, closeTextEditor]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (activeView !== ActiveView.CHAT && !podcastModeRef.current) {
			setIsTextEditorOpen(false);
			setMessageId(null);
			setEditorData({
				id: null,
				title: '',
				content: '',
			});
			setMode(AIAssistantEditorMode.PLAYGROUND);
			dispatch(clearActiveDocument());
		}
	}, [activeView]);

	useEffect(() => {
		if (!isExtended && isTextEditorOpen && !isEditModeFromUrl && !podcastModeRef.current) {
			closeTextEditor();
		}
	}, [isTextEditorOpen, isExtended, isEditModeFromUrl, closeTextEditor]);

	return (
		<aiAssistantEditorContext.Provider
			value={{
				isTextEditorOpen,
				mode,
				openTextEditor,
				openPodcastDetails,
				openPodcast,
				podcastDetails,
				autoPlayPodcast,
				consumeAutoPlayPodcast,
				closeTextEditor,
				messageId,
				documentId,
				activePodcastId,
				editorCloseScrollTarget,
				clearEditorCloseScrollTarget,
				activeDocumentCardRef,
				activeImageRef,
				imageScrollTarget,
				clearImageScrollTarget,
				editorData,
				setTextEditorContent,
				textEditorRef,
				hasUnsavedChanges,
				dismissChangeIndicator,
				markContentChanged,
				markTitleChanged,
				updatePodcastTitle,
				flushPodcastTitleSave,
			}}
		>
			{children}
		</aiAssistantEditorContext.Provider>
	);
};

export default AIAssistantEditorProvider;
